import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  FileText, Plus, Loader2, Send, CreditCard, ArrowRight,
  ChevronDown, X, DollarSign, Trash2, CheckCircle, Clock,
  AlertCircle, FileCheck, PenLine
} from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { useTechPermissions, allowed, enabled } from "../../contexts/TechPermissionsContext";
import { SendForSignatureModal, SignatureStatusBadge, useSignaturePolling } from "../../components/tech/SendForSignatureModal";
import { TechCollectPaymentModal } from "../../components/tech/TechCollectPaymentModal";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "#6B7280", bg: "rgba(107,114,128,0.1)" },
  sent: { label: "Sent", color: "#3B82F6", bg: "rgba(59,130,246,0.1)" },
  paid: { label: "Paid", color: "#22C55E", bg: "rgba(34,197,94,0.1)" },
  partially_paid: { label: "Partially Paid", color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
  overdue: { label: "Overdue", color: "#EF4444", bg: "rgba(239,68,68,0.1)" },
  cancelled: { label: "Cancelled", color: "#6B7280", bg: "rgba(107,114,128,0.1)" },
  accepted: { label: "Accepted", color: "#22C55E", bg: "rgba(34,197,94,0.1)" },
  declined: { label: "Declined", color: "#EF4444", bg: "rgba(239,68,68,0.1)" },
  converted: { label: "Converted", color: "#A855F7", bg: "rgba(168,85,247,0.1)" },
};

const TechDocuments = () => {
  const { isDark } = useTheme();
  const { permissions } = useTechPermissions();
  const [docs, setDocs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState("estimate");
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [sigDoc, setSigDoc] = useState(null); // doc for Send-for-Signature modal

  // Form state
  const [selectedJobId, setSelectedJobId] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0, discount: 0 }]);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");

  // Record payment (deposit / partial) modal state
  const [payDoc, setPayDoc] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const [payNote, setPayNote] = useState("");
  const [paySyncJob, setPaySyncJob] = useState(false);
  const [paySaving, setPaySaving] = useState(false);

  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("tech_token")}` }
  });

  const fetchDocs = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/api/tech/documents`, getAuthConfig());
      setDocs(res.data.documents || []);
    } catch { /* ignore */ } finally { if (!silent) setLoading(false); }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/tech/jobs`, getAuthConfig());
      setJobs(res.data.jobs || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchDocs(); fetchJobs(); }, [fetchDocs, fetchJobs]);

  // Live signature updates — poll while any doc is awaiting a signature
  useSignaturePolling(docs, fetchDocs);

  const calcTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      const lineTotal = (item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount || 0) / 100);
      return sum + lineTotal;
    }, 0);
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;
    return { subtotal: Math.round(subtotal * 100) / 100, taxAmount: Math.round(taxAmount * 100) / 100, total: Math.round(total * 100) / 100 };
  };

  const resetForm = () => {
    setSelectedJobId("");
    setItems([{ description: "", quantity: 1, unit_price: 0, discount: 0 }]);
    setTaxRate(0);
    setNotes("");
    setPaymentTerms("");
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!selectedJobId) { toast.error("Please select a job"); return; }
    if (items.length === 0 || !items[0].description) { toast.error("Add at least one item"); return; }

    const { subtotal, taxAmount, total } = calcTotals();
    if (total <= 0) { toast.error("Total must be greater than 0"); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/api/tech/documents`, {
        job_id: selectedJobId,
        document_type: formType,
        items,
        subtotal,
        discount_total: items.reduce((s, i) => s + ((i.quantity || 0) * (i.unit_price || 0) * (i.discount || 0) / 100), 0),
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
        notes: notes || null,
        payment_terms: paymentTerms || null,
      }, getAuthConfig());
      toast.success(`${formType === "estimate" ? "Estimate" : "Invoice"} created!`);
      resetForm();
      fetchDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create document");
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (docId, status) => {
    setActionLoading(docId);
    try {
      await axios.patch(`${API}/api/tech/documents/${docId}/status`, { status }, getAuthConfig());
      toast.success(`Status updated to ${status}`);
      fetchDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally { setActionLoading(null); }
  };

  const handleConvert = async (estimateId) => {
    setActionLoading(estimateId);
    try {
      const res = await axios.post(`${API}/api/tech/documents/${estimateId}/convert`, {}, getAuthConfig());
      toast.success(`Converted to Invoice ${res.data.document?.invoice_number}`);
      fetchDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert");
    } finally { setActionLoading(null); }
  };

  // Collect Payment (Stripe card entry / payment link) modal state
  const [collectDoc, setCollectDoc] = useState(null);

  const openPayModal = (doc) => {
    const bal = Math.max((doc.total || 0) - (doc.amount_paid || 0), 0);
    setPayDoc(doc);
    setPayAmount(bal > 0 ? bal.toFixed(2) : "");
    setPayMethod("Cash");
    setPayNote("");
    setPaySyncJob(false);
  };

  const handleRecordPayment = async () => {
    const amt = parseFloat(payAmount) || 0;
    const bal = Math.max((payDoc?.total || 0) - (payDoc?.amount_paid || 0), 0);
    if (amt <= 0) { toast.error("Enter a payment amount greater than zero"); return; }
    if (amt > bal + 0.005) { toast.error(`Amount exceeds the remaining balance ($${bal.toFixed(2)})`); return; }
    setPaySaving(true);
    try {
      const res = await axios.post(`${API}/api/tech/documents/${payDoc.invoice_id}/payments`, {
        amount: amt,
        method: payMethod,
        note: payNote.trim() || undefined,
        mark_job_deposit_collected: paySyncJob,
      }, getAuthConfig());
      toast.success(res.data?.fully_paid ? "Payment recorded — invoice fully paid!" : "Deposit recorded");
      if (res.data?.job_status_updated) toast.info("Job marked as Deposit Collected");
      setPayDoc(null);
      fetchDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally { setPaySaving(false); }
  };

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, discount: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: field === "description" ? value : parseFloat(value) || 0 };
    setItems(updated);
  };

  const filteredDocs = docs.filter(d => {
    if (tab === "all") return true;
    if (tab === "estimates") return d.document_type === "estimate";
    if (tab === "invoices") return d.document_type === "invoice";
    return true;
  });

  const { subtotal, taxAmount, total } = calcTotals();

  // Theme-aware classes
  const cardBg = isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200 shadow-sm";
  const inputCls = isDark
    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FFCC00]/30"
    : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-400";
  const selectCls = isDark
    ? "bg-white/[0.04] border-white/[0.08] text-white"
    : "bg-white border-gray-300 text-gray-900";
  const optionBg = isDark ? "bg-[#0c0c14]" : "bg-white";
  const modalBg = isDark ? "bg-[#0c0c14] border-white/[0.08]" : "bg-white border-gray-200 shadow-xl";
  const tabsBg = isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-100 border-gray-200";
  const tabActive = isDark ? "bg-[#FFCC00]/15 text-[#FFCC00]" : "bg-amber-100 text-amber-700";
  const tabInactive = isDark ? "text-white/40 hover:text-white/60" : "text-gray-500 hover:text-gray-700";
  const totalsBg = isDark ? "bg-white/[0.02] border-white/[0.05]" : "bg-gray-50 border-gray-200";
  const divider = isDark ? "border-white/[0.04]" : "border-gray-100";

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-7 h-7 text-[#FFCC00] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tech-documents">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground font-['Manrope']">Estimates & Invoices</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Create and manage documents for your jobs</p>
        </div>
        <div className="flex gap-2">
          {allowed(permissions, "create_estimates") && (
          <Button
            onClick={() => { setFormType("estimate"); setShowForm(true); }}
            size="sm"
            className="h-9 rounded-xl bg-orange-500/15 hover:bg-orange-500/25 text-orange-500 border border-orange-500/20"
            data-testid="new-estimate-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Estimate
          </Button>
          )}
          {allowed(permissions, "create_invoices") && (
          <Button
            onClick={() => { setFormType("invoice"); setShowForm(true); }}
            size="sm"
            className="h-9 rounded-xl bg-[#FFCC00]/15 hover:bg-[#FFCC00]/25 text-[#FFCC00] border border-[#FFCC00]/20"
            data-testid="new-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Invoice
          </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl w-fit border ${tabsBg}`}>
        {[
          { key: "all", label: "All" },
          { key: "estimates", label: "Estimates" },
          { key: "invoices", label: "Invoices" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
              tab === t.key ? tabActive : tabInactive
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDark ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm`} onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
          <div className={`w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl p-5 space-y-4 border ${modalBg}`} data-testid="document-form">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground font-['Manrope']">
                New {formType === "estimate" ? "Estimate" : "Invoice"}
              </h2>
              <button onClick={resetForm} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            {/* Job selector */}
            <div>
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1.5 block">Select Job</label>
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className={`w-full h-10 px-3 rounded-xl text-sm focus:outline-none ${selectCls}`}
                data-testid="job-select"
              >
                <option value="" className={optionBg}>Choose a job...</option>
                {jobs.filter(j => j.assigned_to || j.assignment_status === "accepted").map((j) => (
                  <option key={j.job_id} value={j.job_id} className={optionBg}>
                    {j.title || j.service_name || "Untitled"} — {j.customer_name || "No customer"}
                  </option>
                ))}
              </select>
            </div>

            {/* Line items */}
            <div>
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1.5 block">Items</label>
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`line-item-${idx}`}>
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem(idx, "description", e.target.value)}
                      className={`flex-1 h-9 px-3 rounded-lg text-sm focus:outline-none ${inputCls}`}
                      data-testid={`item-desc-${idx}`}
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                      className={`w-16 h-9 px-2 rounded-lg text-sm text-center focus:outline-none ${inputCls}`}
                      data-testid={`item-qty-${idx}`}
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                      className={`w-24 h-9 px-2 rounded-lg text-sm focus:outline-none ${inputCls}`}
                      data-testid={`item-price-${idx}`}
                    />
                    {items.length > 1 && (
                      <button onClick={() => removeItem(idx)} className="text-red-400/50 hover:text-red-400 mt-2"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addItem} className="text-[#FFCC00]/60 hover:text-[#FFCC00] text-xs mt-2 flex items-center gap-1" data-testid="add-item-btn">
                <Plus className="w-3 h-3" /> Add item
              </button>
            </div>

            {/* Tax + Notes */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1.5 block">Tax Rate (%)</label>
                <input
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                  className={`w-full h-9 px-3 rounded-lg text-sm focus:outline-none ${inputCls}`}
                  data-testid="tax-rate-input"
                />
              </div>
              <div className="flex-1">
                <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1.5 block">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional"
                  className={`w-full h-9 px-3 rounded-lg text-sm focus:outline-none ${inputCls}`}
                  data-testid="notes-input"
                />
              </div>
            </div>

            {/* Totals */}
            <div className={`rounded-xl p-3 space-y-1.5 border ${totalsBg}`}>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="text-foreground">${subtotal.toFixed(2)}</span></div>
              {taxRate > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax ({taxRate}%)</span><span className="text-foreground">${taxAmount.toFixed(2)}</span></div>}
              <div className={`flex justify-between text-base font-bold border-t pt-1.5 ${divider}`}><span className="text-foreground">Total</span><span className="text-[#FFCC00]">${total.toFixed(2)}</span></div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button onClick={resetForm} variant="ghost" className={`flex-1 h-10 rounded-xl border ${isDark ? "text-white/40 hover:text-white/60 border-white/[0.06]" : "text-gray-500 hover:text-gray-700 border-gray-200"}`} data-testid="cancel-form-btn">Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={saving}
                className="flex-1 h-10 rounded-xl bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold"
                data-testid="save-document-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileText className="w-4 h-4 mr-1.5" />}
                Create {formType === "estimate" ? "Estimate" : "Invoice"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {filteredDocs.length === 0 ? (
        <div className={`text-center py-16 rounded-2xl border ${cardBg}`}>
          <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
          <p className="text-muted-foreground text-sm">No documents yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Create an estimate or invoice for your jobs</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDocs.map((doc) => {
            const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
            const isEstimate = doc.document_type === "estimate";
            const isLoading = actionLoading === doc.invoice_id;

            return (
              <div
                key={doc.invoice_id}
                className={`rounded-2xl p-4 transition-all duration-200 hover:translate-y-[-1px] border ${cardBg}`}
                data-testid={`doc-card-${doc.invoice_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-semibold text-sm font-['Manrope']">
                        {doc.invoice_number}
                      </span>
                      <Badge className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}20` }}>
                        {sc.label}
                      </Badge>
                      <Badge className="text-[10px] px-2 py-0.5 rounded-full" style={{
                        background: isEstimate ? "rgba(249,115,22,0.1)" : "rgba(59,130,246,0.1)",
                        color: isEstimate ? "#F97316" : "#3B82F6",
                        border: `1px solid ${isEstimate ? "#F97316" : "#3B82F6"}20`
                      }}>
                        {isEstimate ? "Estimate" : "Invoice"}
                      </Badge>
                      <SignatureStatusBadge status={doc.signature_display_status} />
                    </div>
                    <p className="text-muted-foreground text-xs mt-1 truncate">
                      {doc.customer_name || "No customer"} {doc.job_id && `\u00B7 Job: ${doc.job_id.slice(0, 12)}`}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-foreground font-bold text-lg font-['Manrope']">${(doc.total || 0).toFixed(2)}</p>
                    {(doc.amount_paid || 0) > 0 && doc.status !== "paid" && (
                      <p className="text-[10px] text-amber-500 font-medium" data-testid={`tech-doc-balance-${doc.invoice_id}`}>
                        Paid ${(doc.amount_paid || 0).toFixed(2)} · Bal ${Math.max((doc.total || 0) - (doc.amount_paid || 0), 0).toFixed(2)}
                      </p>
                    )}
                    <p className="text-muted-foreground/50 text-[10px]">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ""}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className={`flex flex-wrap gap-2 mt-3 pt-3 border-t ${divider}`}>
                  {doc.status === "draft" && allowed(permissions, isEstimate ? "send_estimates" : "send_invoices") && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusChange(doc.invoice_id, "sent")}
                      disabled={isLoading}
                      className="h-7 text-[11px] rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 border border-blue-500/15"
                      data-testid={`send-btn-${doc.invoice_id}`}
                    >
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                      Send
                    </Button>
                  )}

                  {enabled(permissions, isEstimate ? "send_estimates_for_signature" : "send_invoices_for_signature")
                    && doc.signature_display_status !== "signed"
                    && !["converted", "declined", "cancelled"].includes(doc.status) && (
                    <Button
                      size="sm"
                      onClick={() => setSigDoc(doc)}
                      disabled={isLoading}
                      className="h-7 text-[11px] rounded-lg bg-[#FFCC00]/10 hover:bg-[#FFCC00]/20 text-[#FFCC00] border border-[#FFCC00]/15"
                      data-testid={`signature-btn-${doc.invoice_id}`}
                    >
                      <PenLine className="w-3 h-3 mr-1" />
                      {["sent", "viewed", "expired"].includes(doc.signature_display_status) ? "Resend Signature" : "Request Signature"}
                    </Button>
                  )}

                  {isEstimate && !["converted", "declined"].includes(doc.status) && allowed(permissions, "create_invoices") && (
                    <Button
                      size="sm"
                      onClick={() => handleConvert(doc.invoice_id)}
                      disabled={isLoading}
                      className="h-7 text-[11px] rounded-lg bg-purple-500/10 hover:bg-purple-500/20 text-purple-500 border border-purple-500/15"
                      data-testid={`convert-btn-${doc.invoice_id}`}
                    >
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                      Convert to Invoice
                    </Button>
                  )}

                  {!isEstimate && !["paid", "cancelled"].includes(doc.status) && ((doc.total || 0) - (doc.amount_paid || 0)) > 0.005 && (allowed(permissions, "collect_payments") || allowed(permissions, "collect_deposits")) && (
                    <Button
                      size="sm"
                      onClick={() => setCollectDoc(doc)}
                      disabled={isLoading}
                      className="h-7 text-[11px] rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/15"
                      data-testid={`collect-btn-${doc.invoice_id}`}
                    >
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3 mr-1" />}
                      Collect Payment
                    </Button>
                  )}

                  {!isEstimate && !["paid", "cancelled"].includes(doc.status) && (allowed(permissions, "collect_deposits") || allowed(permissions, "collect_payments")) && (
                    <Button
                      size="sm"
                      onClick={() => openPayModal(doc)}
                      disabled={isLoading}
                      className="h-7 text-[11px] rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border border-amber-500/15"
                      data-testid={`record-payment-btn-${doc.invoice_id}`}
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      Record Payment
                    </Button>
                  )}

                  {!isEstimate && ["sent", "partially_paid"].includes(doc.status) && allowed(permissions, "collect_payments") && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusChange(doc.invoice_id, "paid")}
                      disabled={isLoading}
                      className="h-7 text-[11px] rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/15"
                      data-testid={`mark-paid-btn-${doc.invoice_id}`}
                    >
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                      Mark Paid
                    </Button>
                  )}

                  {doc.status === "paid" && (
                    <span className="flex items-center gap-1 text-emerald-500 text-[11px]">
                      <CheckCircle className="w-3 h-3" /> Paid {doc.paid_at ? `on ${new Date(doc.paid_at).toLocaleDateString()}` : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Send For Signature Modal */}
      {sigDoc && (
        <SendForSignatureModal
          doc={sigDoc}
          isDark={isDark}
          onClose={() => setSigDoc(null)}
          onSent={() => fetchDocs(true)}
        />
      )}

      {/* Collect Payment Modal (Stripe card entry / payment link) */}
      {collectDoc && (
        <TechCollectPaymentModal
          doc={collectDoc}
          isDark={isDark}
          onClose={() => { setCollectDoc(null); fetchDocs(true); }}
          onPaid={(upd) => {
            setDocs((prev) => prev.map((d) => d.invoice_id === collectDoc.invoice_id
              ? { ...d, status: upd.status, amount_paid: upd.amount_paid, balance_due: upd.balance_due }
              : d));
          }}
        />
      )}

      {/* Record Payment Modal */}
      {payDoc && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDark ? "bg-black/70" : "bg-black/40"} backdrop-blur-sm`} onClick={(e) => { if (e.target === e.currentTarget) setPayDoc(null); }}>
          <div className={`w-full max-w-sm rounded-2xl p-5 space-y-4 border ${modalBg}`} data-testid="tech-payment-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground font-['Manrope']">Record Payment</h2>
              <button onClick={() => setPayDoc(null)} className="text-muted-foreground hover:text-foreground" data-testid="tech-payment-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className={`grid grid-cols-3 gap-2 rounded-xl p-3 text-center border ${totalsBg}`}>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
                <p className="text-sm font-bold text-foreground">${(payDoc.total || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</p>
                <p className="text-sm font-bold text-emerald-500">${(payDoc.amount_paid || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Remaining</p>
                <p className="text-sm font-bold text-amber-500">${Math.max((payDoc.total || 0) - (payDoc.amount_paid || 0), 0).toFixed(2)}</p>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Amount *</label>
              <input
                type="number" step="0.01" min="0"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className={`w-full h-10 rounded-lg px-3 text-base font-semibold border ${inputCls}`}
                data-testid="tech-payment-amount"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Method</label>
              <select
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
                className={`w-full h-9 rounded-lg px-2 text-sm border ${selectCls}`}
                data-testid="tech-payment-method"
              >
                {["Cash", "Check", "Credit Card", "Debit Card", "Zelle", "Venmo", "Bank Transfer", "Other"].map(m => (
                  <option key={m} value={m} className={optionBg}>{m}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Note (optional)</label>
              <input
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                placeholder="e.g. Deposit to order parts"
                className={`w-full h-9 rounded-lg px-3 text-sm border ${inputCls}`}
                data-testid="tech-payment-note"
              />
            </div>
            {payDoc.job_id && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={paySyncJob}
                  onChange={(e) => setPaySyncJob(e.target.checked)}
                  className="w-4 h-4 accent-teal-500"
                  data-testid="tech-payment-sync-job"
                />
                <span className="text-sm text-foreground">Mark job as Deposit Collected</span>
              </label>
            )}
            <Button
              onClick={handleRecordPayment}
              disabled={paySaving || !(parseFloat(payAmount) > 0)}
              className="w-full h-10 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-500 border border-emerald-500/20"
              data-testid="tech-payment-submit"
            >
              {paySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4 mr-1" />}
              Record ${(parseFloat(payAmount) || 0).toFixed(2)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechDocuments;
